(function () {
  'use strict';

  const STORAGE_KEY = 'wd_members_club_pending_registration';

  class WdMembersClubAuth extends HTMLElement {
    connectedCallback() {
      this.loginView = this.querySelector('[data-view="login"]');
      this.registerView = this.querySelector('[data-view="register"]');
      this.loginForm = this.querySelector('[data-form="login"]');
      this.registerForm = this.querySelector('[data-form="register"]');
      this.redirectBase = this.dataset.redirectUrl || '/customer_authentication/login';
      this.pendingUrl = this.dataset.pendingUrl || '/apps/members-club/pending';
      this.errors = {
        email: this.dataset.errorEmail || '',
        required: this.dataset.errorRequired || '',
        phone: this.dataset.errorPhone || '',
        dobFormat: this.dataset.errorDobFormat || '',
        terms: this.dataset.errorTerms || '',
        phoneTaken: this.dataset.errorPhoneTaken || '',
      };

      this.bindViewSwitch();
      this.bindForms();
      this.bindPolicyModals();

      const initialView =
        window.location.hash === '#register'
          ? 'register'
          : this.dataset.initialView === 'register'
            ? 'register'
            : 'login';
      this.showView(initialView);
    }

    bindViewSwitch() {
      this.querySelectorAll('[data-switch-view]').forEach((button) => {
        button.addEventListener('click', () => {
          const view = button.dataset.switchView;
          this.showView(view);
          if (view === 'register') {
            window.history.replaceState(null, '', '#register');
          } else {
            window.history.replaceState(null, '', window.location.pathname);
          }
        });
      });
    }

    showView(view) {
      [this.loginView, this.registerView].forEach((panel) => {
        if (!panel) return;
        const isActive = panel.dataset.view === view;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.querySelectorAll('[data-switch-view]').forEach((button) => {
        const isActive = button.dataset.switchView === view;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });
    }

    bindForms() {
      if (this.loginForm) {
        this.loginForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.handleLoginSubmit();
        });
        this.bindLiveFieldClear(this.loginForm);
      }

      if (this.registerForm) {
        this.registerForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.handleRegisterSubmit();
        });
        this.bindLiveFieldClear(this.registerForm);
        this.bindDobMask(this.registerForm.querySelector('[data-dob-mask]'));
      }
    }

    /** Type-friendly DD/MM/YYYY mask (no native date picker — awkward for birth years). */
    bindDobMask(input) {
      if (!input) return;

      input.addEventListener('input', () => {
        const prev = input.value;
        const caret = input.selectionStart ?? prev.length;
        const digitsBeforeCaret = prev.slice(0, caret).replace(/\D/g, '').length;
        const formatted = this.formatDobDigits(prev);

        if (formatted === prev) return;
        input.value = formatted;

        let pos = 0;
        let seen = 0;
        while (pos < formatted.length && seen < digitsBeforeCaret) {
          if (/\d/.test(formatted.charAt(pos))) seen += 1;
          pos += 1;
        }
        if (formatted.charAt(pos) === '/') pos += 1;
        input.setSelectionRange(pos, pos);
      });

      input.addEventListener('blur', () => {
        const value = input.value.trim();
        if (!value) return;
        const normalized = this.normalizeDateOfBirth(value);
        if (normalized) {
          const [year, month, day] = normalized.split('-');
          input.value = `${day}/${month}/${year}`;
        }
      });
    }

    formatDobDigits(raw) {
      const digits = String(raw || '')
        .replace(/\D/g, '')
        .slice(0, 8);
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }

    /** Clear field / terms errors as the shopper corrects the form. */
    bindLiveFieldClear(form) {
      form.querySelectorAll('.wd-mca__input').forEach((input) => {
        const clear = () => this.clearFieldError(input);
        input.addEventListener('input', clear);
        input.addEventListener('change', clear);
      });

      const termsInput = form.querySelector('[name="terms_accepted"]');
      const termsField = form.querySelector('[data-terms-field]');
      if (termsInput && termsField) {
        termsInput.addEventListener('change', () => {
          if (termsInput.checked) termsField.textContent = '';
        });
      }
    }

    bindPolicyModals() {
      this.querySelectorAll('[data-policy-open]').forEach((button) => {
        button.addEventListener('click', () => {
          const modalId = button.getAttribute('aria-controls');
          const modal = modalId ? document.getElementById(modalId) : null;
          if (!modal || typeof modal.show !== 'function') return;
          modal.show(button);
        });
      });
    }

    handleLoginSubmit() {
      const emailInput = this.loginForm.querySelector('[name="email"]');
      const email = emailInput?.value.trim() || '';

      if (!this.isValidEmail(email)) {
        this.setFieldError(emailInput, this.errors.email);
        return;
      }

      this.clearFieldError(emailInput);
      this.redirectToAuth(email);
    }

    async handleRegisterSubmit() {
      const formData = new FormData(this.registerForm);
      const payload = {
        firstName: String(formData.get('first_name') || '').trim(),
        lastName: String(formData.get('last_name') || '').trim(),
        phone: String(formData.get('phone') || '').trim(),
        dateOfBirth: String(formData.get('date_of_birth') || '').trim(),
        email: String(formData.get('email') || '').trim(),
        marketingOptIn: formData.get('marketing_opt_in') === 'on',
        savedAt: new Date().toISOString(),
      };

      const firstNameInput = this.registerForm.querySelector('[name="first_name"]');
      const lastNameInput = this.registerForm.querySelector('[name="last_name"]');
      const phoneInput = this.registerForm.querySelector('[name="phone"]');
      const dobInput = this.registerForm.querySelector('[name="date_of_birth"]');
      const emailInput = this.registerForm.querySelector('[name="email"]');
      const termsField = this.registerForm.querySelector('[data-terms-field]');

      [firstNameInput, lastNameInput, phoneInput, dobInput, emailInput].forEach((input) => {
        this.clearFieldError(input);
      });
      if (termsField) termsField.textContent = '';

      let hasError = false;

      if (!payload.firstName) {
        hasError = true;
        this.setFieldError(firstNameInput, this.errors.required);
      }

      if (!payload.lastName) {
        hasError = true;
        this.setFieldError(lastNameInput, this.errors.required);
      }

      if (!this.isValidPhone(payload.phone)) {
        hasError = true;
        this.setFieldError(phoneInput, payload.phone ? this.errors.phone : this.errors.required);
      }

      if (!payload.dateOfBirth) {
        hasError = true;
        this.setFieldError(dobInput, this.errors.required);
      } else if (!this.normalizeDateOfBirth(payload.dateOfBirth)) {
        hasError = true;
        this.setFieldError(dobInput, this.errors.dobFormat);
      }

      if (!this.isValidEmail(payload.email)) {
        hasError = true;
        this.setFieldError(emailInput, this.errors.email);
      }

      const termsAccepted = formData.get('terms_accepted') === 'on';
      if (!termsAccepted) {
        hasError = true;
        if (termsField) {
          termsField.textContent = this.errors.terms;
        }
      }

      if (hasError) return;

      const normalizedDob = this.normalizeDateOfBirth(payload.dateOfBirth);
      if (normalizedDob) payload.dateOfBirth = normalizedDob;

      const submitButton = this.registerForm.querySelector('[type="submit"]');
      if (submitButton) submitButton.disabled = true;

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        console.warn('wd-members-club-auth: unable to persist registration draft', error);
      }

      try {
        await this.savePendingRegistration(payload);
      } catch (error) {
        const code = error && typeof error === 'object' && 'code' in error ? error.code : '';
        if (code === 'phone_taken') {
          this.setFieldError(
            this.registerForm.querySelector('[name="phone"]'),
            this.errors.phoneTaken
          );
          if (submitButton) submitButton.disabled = false;
          return;
        }
        console.warn('wd-members-club-auth: pending proxy failed; continuing to OTP', error);
      }

      if (submitButton) submitButton.disabled = false;
      this.redirectToAuth(payload.email);
    }

    async savePendingRegistration(payload) {
      const response = await fetch(this.pendingUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email: payload.email,
          firstName: payload.firstName,
          lastName: payload.lastName,
          phone: payload.phone,
          dateOfBirth: payload.dateOfBirth,
          marketingOptIn: payload.marketingOptIn,
        }),
        credentials: 'same-origin',
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        const err = new Error(data.error || `pending_http_${response.status}`);
        err.code = data.error || `pending_http_${response.status}`;
        throw err;
      }
    }

    redirectToAuth(email) {
      const queryReturnTo = new URLSearchParams(window.location.search).get('return_to');
      const returnTo = queryReturnTo || this.dataset.returnTo || window.location.pathname;
      const params = new URLSearchParams({
        login_hint: email,
        return_to: returnTo,
      });

      window.location.href = `${this.redirectBase}?${params.toString()}`;
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    isValidPhone(phone) {
      const digits = String(phone || '').replace(/\D/g, '');
      return digits.length >= 9 && digits.length <= 15;
    }

    normalizeDateOfBirth(raw) {
      const value = String(raw || '').trim();
      if (!value) return null;

      let year;
      let month;
      let day;

      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        [year, month, day] = value.split('-');
      } else {
        const dmy = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(value);
        if (!dmy) return null;
        day = dmy[1].padStart(2, '0');
        month = dmy[2].padStart(2, '0');
        year = dmy[3];
      }

      const y = Number(year);
      const m = Number(month);
      const d = Number(day);
      if (!y || m < 1 || m > 12 || d < 1 || d > 31) return null;

      const date = new Date(Date.UTC(y, m - 1, d));
      if (
        date.getUTCFullYear() !== y ||
        date.getUTCMonth() !== m - 1 ||
        date.getUTCDate() !== d
      ) {
        return null;
      }

      const today = new Date();
      const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
      if (date.getTime() > todayUtc) return null;
      if (y < 1900) return null;

      return `${year}-${month}-${day}`;
    }

    setFieldError(input, message) {
      if (!input) return;
      input.classList.add('is-error');
      input.setAttribute('aria-invalid', 'true');

      const field = input.closest('.wd-mca__field');
      const error = field?.querySelector('[data-field-error]');
      if (error) error.textContent = message;
    }

    clearFieldError(input) {
      if (!input) return;
      input.classList.remove('is-error');
      input.removeAttribute('aria-invalid');

      const field = input.closest('.wd-mca__field');
      const error = field?.querySelector('[data-field-error]');
      if (error) error.textContent = '';
    }
  }

  if (!customElements.get('wd-members-club-auth')) {
    customElements.define('wd-members-club-auth', WdMembersClubAuth);
  }
})();
